import { useState, useEffect } from 'react';
import { BarChart3, TrendingUp, Users, Play, Radio, Route, Loader } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { getUserContentAnalytics, calculateAnalyticsSummary } from '../utils/analytics';

export function AnalyticsDashboard() {
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [events, setEvents] = useState<any[]>([]);
  const [summary, setSummary] = useState<any>(null);

  useEffect(() => {
    if (user) {
      loadAnalytics();
    }
  }, [user]);

  const loadAnalytics = async () => {
    if (!user) return;

    setIsLoading(true);
    try {
      const data = await getUserContentAnalytics(user.id);
      setEvents(data);

      const calculatedSummary = calculateAnalyticsSummary(data);
      setSummary(calculatedSummary);
    } catch (err) {
      console.error('Error loading analytics:', err);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow-lg p-8">
        <div className="flex items-center justify-center">
          <Loader className="w-8 h-8 animate-spin text-[#00c7b7]" />
        </div>
      </div>
    );
  }

  if (!summary) {
    return (
      <div className="bg-white rounded-lg shadow-lg p-8">
        <p className="text-center text-gray-500">No analytics data available</p>
      </div>
    );
  }

  const statCards = [
    {
      title: 'Total Events',
      value: summary.totalEvents,
      icon: BarChart3,
      color: 'blue'
    },
    {
      title: 'Sound Drop Listens',
      value: summary.totalListens,
      icon: Play,
      color: 'green'
    },
    {
      title: 'Zone Entries',
      value: summary.totalZoneEnters,
      icon: Radio,
      color: 'purple'
    },
    {
      title: 'Trail Starts',
      value: summary.totalTrailStarts,
      icon: Route,
      color: 'orange'
    }
  ];

  const eventTypeLabels: Record<string, string> = {
    drop_listen: 'Drop Listens',
    zone_enter: 'Zone Entries',
    zone_exit: 'Zone Exits',
    trail_start: 'Trail Starts',
    trail_complete: 'Trail Completions',
    stop_visit: 'Stop Visits'
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="flex items-center gap-3 mb-6">
          <BarChart3 className="w-6 h-6 text-[#00c7b7]" />
          <h2 className="text-2xl font-bold">Analytics Dashboard</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {statCards.map((stat) => {
            const Icon = stat.icon;
            const colorClasses = {
              blue: 'bg-teal-50 text-[#00c7b7]',
              green: 'bg-green-50 text-green-600',
              purple: 'bg-purple-50 text-purple-600',
              orange: 'bg-orange-50 text-orange-600'
            };

            return (
              <div key={stat.title} className="bg-white border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className={`p-2 rounded-lg ${colorClasses[stat.color as keyof typeof colorClasses]}`}>
                    <Icon className="w-5 h-5" />
                  </div>
                </div>
                <p className="text-2xl font-bold mb-1">{stat.value.toLocaleString()}</p>
                <p className="text-sm text-gray-600">{stat.title}</p>
              </div>
            );
          })}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            <h3 className="text-lg font-semibold mb-4">Events by Type</h3>
            <div className="space-y-3">
              {Object.entries(summary.eventsByType).map(([type, count]) => (
                <div key={type} className="flex items-center justify-between">
                  <span className="text-sm text-gray-300">
                    {eventTypeLabels[type] || type}
                  </span>
                  <div className="flex items-center gap-3">
                    <div className="w-32 h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-[#00c7b7] rounded-full"
                        style={{
                          width: `${((count as number) / summary.totalEvents) * 100}%`
                        }}
                      />
                    </div>
                    <span className="text-sm font-medium w-12 text-right">{count as number}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <h3 className="text-lg font-semibold mb-4">Recent Activity</h3>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {events.slice(0, 10).map((event, index) => (
                <div key={index} className="flex items-center justify-between text-sm p-2 bg-gray-50 rounded">
                  <div>
                    <span className="font-medium">
                      {eventTypeLabels[event.event_type] || event.event_type}
                    </span>
                    <span className="text-gray-500 ml-2">
                      ({event.content_type})
                    </span>
                  </div>
                  <span className="text-xs text-gray-500">
                    {new Date(event.created_at).toLocaleDateString()}
                  </span>
                </div>
              ))}
              {events.length === 0 && (
                <p className="text-center text-gray-500 py-4">No recent activity</p>
              )}
            </div>
          </div>
        </div>

        {summary.avgDuration > 0 && (
          <div className="mt-6 p-4 bg-teal-50 border border-blue-200 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="w-5 h-5 text-[#00c7b7]" />
              <h4 className="font-semibold text-blue-900">Engagement Stats</h4>
            </div>
            <p className="text-sm text-blue-800">
              Average listen duration: <span className="font-bold">{Math.round(summary.avgDuration)} seconds</span>
            </p>
          </div>
        )}
      </div>

      <div className="bg-white rounded-lg shadow-lg p-6">
        <h3 className="text-lg font-semibold mb-4">Activity Timeline</h3>
        <div className="space-y-2">
          {Object.entries(summary.eventsByDay)
            .sort(([a], [b]) => b.localeCompare(a))
            .slice(0, 7)
            .map(([day, count]) => (
              <div key={day} className="flex items-center justify-between">
                <span className="text-sm text-gray-300">
                  {new Date(day).toLocaleDateString('en-US', {
                    weekday: 'short',
                    month: 'short',
                    day: 'numeric'
                  })}
                </span>
                <div className="flex items-center gap-3">
                  <div className="w-48 h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-green-600 rounded-full"
                      style={{
                        width: `${((count as number) / Math.max(...Object.values(summary.eventsByDay) as number[])) * 100}%`
                      }}
                    />
                  </div>
                  <span className="text-sm font-medium w-12 text-right">{count as number}</span>
                </div>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
}
